/**
 * PageCacheCollector - Utility for finding missing pages and collecting cached pages
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides focused functions for cache operations:
 * - Finding which page indices are missing from cache
 * - Collecting cached pages within a specific range
 */

import type { PageLayout } from '../../../core/types/Typesetter';

/**
 * Find page indices that are missing from the cache
 * 
 * Iterates through a range of page indices and identifies which ones
 * are not present in the cache.
 * 
 * @param cache - Page cache Map
 * @param startPage - Starting page index (inclusive)
 * @param endPage - Ending page index (inclusive)
 * @param lookahead - Additional pages to check beyond endPage
 * @param generateKey - Function to generate cache key from page index
 * @returns Array of missing page indices
 */
export function findMissingPageIndices(
  cache: Map<string, PageLayout>,
  startPage: number,
  endPage: number,
  lookahead: number,
  generateKey: (pageIndex: number) => string
): number[] {
  const missing: number[] = [];
  
  for (let pageIdx = startPage; pageIdx <= endPage + lookahead; pageIdx++) {
    const cacheKey = generateKey(pageIdx);
    if (!cache.has(cacheKey)) {
      missing.push(pageIdx);
    }
  }
  
  return missing;
}

/**
 * Collect cached pages within a specific range
 * 
 * Iterates through a range of page indices and collects all pages
 * that exist in the cache. Only collects pages within the specified
 * range (does not include lookahead).
 * 
 * @param cache - Page cache Map
 * @param startPage - Starting page index (inclusive)
 * @param endPage - Ending page index (inclusive)
 * @param generateKey - Function to generate cache key from page index
 * @returns Array of cached PageLayout objects
 */
export function collectCachedPagesInRange(
  cache: Map<string, PageLayout>,
  startPage: number,
  endPage: number,
  generateKey: (pageIndex: number) => string
): PageLayout[] {
  const cachedPages: PageLayout[] = [];
  
  for (let pageIdx = startPage; pageIdx <= endPage; pageIdx++) {
    const cacheKey = generateKey(pageIdx);
    const page = cache.get(cacheKey);
    if (page) {
      cachedPages.push(page);
    }
  }
  
  return cachedPages;
}

